import type { Tournament } from '@brush/api-client';

/**
 * Every tag this room can apply, most-used first.
 *
 * Alphabetical order is the wrong one for a picker. A room runs the same handful of formats week
 * after week and reaches for those on nearly every tournament, while the long tail — one
 * championship a year, a format tried once — is what an alphabetical list puts at the top purely
 * because it starts with a B. Ordering by use puts the answer in the first row.
 *
 * Platform tags are included whether or not any tournament carries them yet: the platform defining
 * one is a statement that it should be reachable, and a brand-new one would otherwise be buried
 * exactly when it most needs finding.
 *
 * Counted by exact spelling, not case-folded. `MIXED` and `Mixed` really are two different tags
 * here — the server stores what it is given — and collapsing them in the picker would offer one
 * spelling while the tournaments carry the other.
 */
export function rankedTags(
  tournaments: readonly Tournament[],
  platformTags: readonly string[],
): string[] {
  const uses = new Map<string, number>();

  for (const tournament of tournaments) {
    for (const tag of tournament.tags) uses.set(tag, (uses.get(tag) ?? 0) + 1);
  }
  for (const tag of platformTags) {
    if (!uses.has(tag)) uses.set(tag, 0);
  }

  return [...uses.entries()]
    .sort(([aTag, aUses], [bTag, bUses]) => bUses - aUses || aTag.localeCompare(bTag))
    .map(([tag]) => tag);
}

/**
 * `rankedTags`, from the room's vocabulary (`GET /api/rooms/{roomId}/tournament-tags`) rather than a
 * count over the whole tournament list — which was capped at 200 and cost the list. Same order: most-used
 * first, ties alphabetical, platform tags present at zero, spellings apart. `tags.test.js` holds the two
 * to the same answer over the same tournaments.
 */
export function rankedTagsFromUsage(
  usage: readonly { tag: string; tournaments: number }[],
  platformTags: readonly string[],
): string[] {
  const uses = new Map<string, number>(usage.map((u) => [u.tag, u.tournaments]));
  for (const tag of platformTags) {
    if (!uses.has(tag)) uses.set(tag, 0);
  }

  return [...uses.entries()]
    .sort(([aTag, aUses], [bTag, bUses]) => bUses - aUses || aTag.localeCompare(bTag))
    .map(([tag]) => tag);
}

/**
 * One tournament's tags in the order a row should show them: the tags the filter requires first,
 * the rest after, each half keeping the order it was already in.
 *
 * A row shows the first few and counts the remainder, so a tournament carrying more tags than fit
 * can truncate away the very tag the list was filtered on — the row then reads as though it does
 * not carry it, which looks like a broken filter rather than a full cell. Lifting the required
 * ones costs nothing: they are on every row in the matched set by construction, so no row loses a
 * tag that distinguishes it from its neighbours.
 *
 * Only the required half is lifted. An excluded tag is absent from every row here, and a tag
 * nobody asked about has no claim on the space.
 *
 * Matched case-insensitively, the way the server compares them — a filter on
 * `shrpo` has to lift a tournament's `SHRPO`, or this fixes nothing for the case that motivated it.
 */
export function tagsForDisplay(tags: readonly string[], required: readonly string[]): string[] {
  if (required.length === 0) return [...tags];

  const wanted = new Set(required.map((tag) => tag.toLowerCase()));
  const isWanted = (tag: string) => wanted.has(tag.toLowerCase());

  return [...tags.filter(isWanted), ...tags.filter((tag) => !isWanted(tag))];
}
